import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfPen,
  PdfColor,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';

/**
 * Dum (House) - House with windows containing images
 *
 * Layout: 2 landscape pages.
 * Page 1: decorated house (3+4 column building, 2 floors) with windows
 * showing right labels. Page 2: answer key with left labels.
 *
 * Migrated from C# EduFrame.Img10.Generators.Dum
 */
export class Dum implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 12;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const h1 = 3;
    const h2 = 4;
    const v = 2;

    const windowW = PdfUnit.FromMillimeters(30);
    const windowH = PdfUnit.FromMillimeters(30);
    const between = PdfUnit.FromMillimeters(5);
    const windowPadding = PdfUnit.FromMillimeters(3);
    const floorHeight = PdfUnit.FromMillimeters(60);
    const topMargin = PdfUnit.FromMillimeters(15);

    const rnd = opts.data.random;

    const pastelHouses = [
      PdfColor.fromRgb(255, 250, 230),
      PdfColor.fromRgb(230, 245, 255),
      PdfColor.fromRgb(255, 240, 240),
      PdfColor.fromRgb(240, 255, 240),
      PdfColor.fromRgb(250, 240, 255),
    ];
    const houseColor = pastelHouses[rnd.next(0, pastelHouses.length)];

    const roofPalettes = [
      {
        c1: PdfColor.fromRgb(255, 140, 140),
        c2: PdfColor.fromRgb(230, 120, 120),
        ch: PdfColor.fromRgb(190, 90, 80),
      },
      {
        c1: PdfColor.fromRgb(140, 180, 255),
        c2: PdfColor.fromRgb(120, 150, 230),
        ch: PdfColor.fromRgb(90, 120, 190),
      },
      {
        c1: PdfColor.fromRgb(255, 180, 120),
        c2: PdfColor.fromRgb(230, 150, 100),
        ch: PdfColor.fromRgb(190, 110, 80),
      },
      {
        c1: PdfColor.fromRgb(160, 210, 160),
        c2: PdfColor.fromRgb(130, 180, 130),
        ch: PdfColor.fromRgb(100, 150, 100),
      },
      {
        c1: PdfColor.fromRgb(200, 160, 220),
        c2: PdfColor.fromRgb(170, 130, 190),
        ch: PdfColor.fromRgb(140, 100, 160),
      },
    ];
    const rPal = roofPalettes[rnd.next(0, roofPalettes.length)];
    const roofColor1 = rPal.c1;
    const roofColor2 = rPal.c2;
    const chimneyColor = rPal.ch;

    const doorColor = PdfColor.fromRgb(220, 160, 120);
    const windowColor = PdfColor.fromRgb(230, 245, 255);
    const windowFrameColor = PdfColor.fromRgb(255, 255, 255);
    const grassColor = PdfColor.fromRgb(210, 245, 210);
    const bushColor1 = PdfColor.fromRgb(150, 210, 150);
    const bushColor2 = PdfColor.fromRgb(130, 190, 130);
    const pathColor = PdfColor.fromRgb(235, 220, 200);

    const hasAttic = rnd.next(0, 2) === 0;
    const hasSmoke = rnd.next(0, 2) === 0;
    const handleOnLeft = rnd.next(0, 2) === 0;

    const pageW = PdfSize.A4H.width;
    const pageH = PdfSize.A4H.height;

    const p1W = h1 * windowW + (h1 + 1) * between;
    const p2W = h2 * windowW + (h2 + 1) * between;
    const bH = floorHeight * v;

    const originX = (pageW - p1W - p2W) / 2;

    const r1 = new PdfRect(originX, pageH - margin - bH, p1W, bH);
    const r2 = new PdfRect(r1.right, r1.y, p2W, bH);

    interface Part {
      x: number;
      y: number;
      point: PdfPoint;
      fill: number;
      index: number;
    }
    const NONE = 0,
      WINDOW = 1,
      DOOR = 2;

    const parts: Part[] = [];
    const door = opts.data.random.next(h1, h2);

    for (let y = 0; y < v; y++) {
      for (let x = 0; x < h1 + h2; x++) {
        let px: number;
        if (x < h1) {
          px = originX + between + x * (between + windowW);
        } else {
          px = originX + r1.width + between + (x - h1) * (between + windowW);
        }
        const py = r1.bottom - (y + 1) * floorHeight;

        parts.push({
          x,
          y,
          point: new PdfPoint(px, py),
          fill: y === 0 && x === door ? DOOR : NONE,
          index: -1,
        });
      }
    }

    const indices = Array.from({ length: parts.length }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = opts.data.random.next(0, i + 1);
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    const windowIndices = indices.slice(0, Math.min(opts.list.length, parts.length - 1));

    for (let i = 0; i < windowIndices.length; i++) {
      parts[windowIndices[i]].index = i;
      parts[windowIndices[i]].fill = WINDOW;
    }

    const page1 = pdf.addPage(true);
    const page2 = pdf.addPage(true);

    page1.drawRectangle(
      null,
      new PdfBrush(grassColor),
      new PdfRect(0, pageH - floorHeight / 2, pageW, floorHeight / 2)
    );

    const doorPart = parts.find((p) => p.fill === DOOR);
    if (doorPart) {
      const doorXCenter = doorPart.point.x + windowW / 2;
      const pathRoad = [
        new PdfPoint(doorXCenter - 15, r1.bottom),
        new PdfPoint(doorXCenter + 15, r1.bottom),
        new PdfPoint(doorXCenter + 40, pageH),
        new PdfPoint(doorXCenter - 40, pageH),
      ];
      page1.drawPolygon(null, new PdfBrush(pathColor), pathRoad);
      page1.drawLines(new PdfPen(PdfColor.fromRgb(220, 200, 180), 3), [pathRoad[0], pathRoad[3]]);
      page1.drawLines(new PdfPen(PdfColor.fromRgb(220, 200, 180), 3), [pathRoad[1], pathRoad[2]]);
    }

    const chimW = PdfUnit.FromMillimeters(16);
    const chimH = PdfUnit.FromMillimeters(35);
    const chimX = r1.centerX - PdfUnit.FromMillimeters(30);
    const chimY = r1.top - (r1.centerX - r1.x) - PdfUnit.FromMillimeters(5);
    const chimneyRect = new PdfRect(chimX, chimY, chimW, chimH);
    const chimTopRect = new PdfRect(chimX - 3, chimY - 4, chimW + 6, 8);

    if (hasSmoke) {
      page1.drawEllipse(
        null,
        new PdfBrush(PdfColor.fromRgb(240, 240, 240)),
        new PdfRect(chimX - 5, chimY - 15, 15, 15)
      );
      page1.drawEllipse(
        null,
        new PdfBrush(PdfColor.fromRgb(245, 245, 245)),
        new PdfRect(chimX - 15, chimY - 28, 20, 20)
      );
      page1.drawEllipse(
        null,
        new PdfBrush(PdfColor.fromRgb(250, 250, 250)),
        new PdfRect(chimX - 25, chimY - 45, 25, 25)
      );
    }

    page1.drawRectangle(GUtil.basePen, new PdfBrush(chimneyColor), chimneyRect);
    page1.drawRoundedRectangle(GUtil.basePen, new PdfBrush(chimneyColor), chimTopRect, 2);

    page1.drawRectangle(GUtil.basePen, new PdfBrush(houseColor), r2);
    page1.drawRectangle(GUtil.basePen, new PdfBrush(houseColor), r1);

    const oh = PdfUnit.FromMillimeters(6);
    const ohY = PdfUnit.FromMillimeters(3);

    const roof1: PdfPoint[] = [
      new PdfPoint(r1.left - oh, r1.top + ohY),
      new PdfPoint(r1.centerX, r1.top - (r1.centerX - r1.x) - oh),
      new PdfPoint(r1.right + oh, r1.top + ohY),
    ];

    const dx = roof1[1].x - roof1[2].x;
    const dy = roof1[1].y - roof1[2].y;
    const roof2: PdfPoint[] = [
      roof1[1],
      new PdfPoint(r2.right + oh + dx, r2.top + ohY + dy),
      new PdfPoint(r2.right + oh, r2.top + ohY),
      roof1[2],
    ];

    page1.drawPolygon(GUtil.basePen, new PdfBrush(roofColor2), roof2);
    page1.drawPolygon(GUtil.basePen, new PdfBrush(roofColor1), roof1);
    page1.drawPolygon(new PdfPen(roofColor2, 4), null, roof1);

    if (hasAttic) {
      const atticR = PdfUnit.FromMillimeters(8);
      const atticRect = new PdfRect(
        r1.centerX - atticR,
        r1.top - (r1.centerX - r1.x) / 2 - atticR + 5,
        atticR * 2,
        atticR * 2
      );
      const atticInnerRect = new PdfRect(
        atticRect.x + 2,
        atticRect.y + 2,
        atticRect.width - 4,
        atticRect.height - 4
      );
      page1.drawEllipse(GUtil.basePen, new PdfBrush(houseColor), atticRect);
      // page1.drawEllipse(GUtil.basePen, new PdfBrush(windowColor), atticInnerRect);
      page1.drawLine(
        GUtil.basePen,
        new PdfPoint(atticRect.centerX, atticRect.top),
        new PdfPoint(atticRect.centerX, atticRect.bottom)
      );
      page1.drawLine(
        GUtil.basePen,
        new PdfPoint(atticRect.left, atticRect.centerY),
        new PdfPoint(atticRect.right, atticRect.centerY)
      );
    }

    const cellSize = new PdfSize(windowW, windowH);
    const leftTexts = opts.list.map((item) => item.left.getText()).filter((t) => t);
    const rightTexts = opts.list.map((item) => item.right.getText()).filter((t) => t);
    const leftFont =
      leftTexts.length > 0
        ? page2.adjustFontMulti(
            leftTexts.map((t) => [t]),
            GUtil.baseFont,
            cellSize
          )
        : GUtil.baseFont;
    const rightFont =
      rightTexts.length > 0
        ? page1.adjustFontMulti(
            rightTexts.map((t) => [t]),
            GUtil.baseFont,
            cellSize
          )
        : GUtil.baseFont;

    for (const part of parts) {
      const p = new PdfRect(part.point.x, part.point.y, windowW, floorHeight);

      if (part.fill === DOOR) {
        const doorRect = new PdfRect(p.x, p.y + topMargin, p.width, p.height - topMargin);
        page1.drawRoundedRectangle(GUtil.basePen, new PdfBrush(doorColor), doorRect, 4);

        const handleR = PdfUnit.FromMillimeters(2);
        const handleX = handleOnLeft
          ? doorRect.left + PdfUnit.FromMillimeters(6) - handleR
          : doorRect.right - PdfUnit.FromMillimeters(6) - handleR;
        const handleRect = new PdfRect(
          handleX,
          doorRect.centerY - handleR,
          handleR * 2,
          handleR * 2
        );
        page1.drawEllipse(GUtil.basePen, new PdfBrush(PdfColor.fromRgb(100, 70, 50)), handleRect);
      } else if (part.fill === WINDOW) {
        const winRect = new PdfRect(p.x, p.y + topMargin, windowW, windowH);

        const sillRect = new PdfRect(winRect.left - 3, winRect.bottom, winRect.width + 6, 4);
        page1.drawRoundedRectangle(
          GUtil.basePen,
          new PdfBrush(PdfColor.fromRgb(200, 160, 130)),
          sillRect,
          2
        );

        const innerW = (winRect.width - 3 * windowPadding) / 2;
        const innerH = winRect.height - 2 * windowPadding;
        const inner1 = new PdfRect(
          winRect.x + windowPadding,
          winRect.y + windowPadding,
          innerW,
          innerH
        );
        const inner2 = new PdfRect(
          winRect.x + innerW + windowPadding * 2,
          winRect.y + windowPadding,
          innerW,
          innerH
        );

        page1.drawRoundedRectangle(GUtil.basePen, new PdfBrush(windowFrameColor), winRect, 3);
        page1.drawRoundedRectangle(GUtil.basePen, new PdfBrush(windowColor), inner1, 2);
        page1.drawRoundedRectangle(GUtil.basePen, new PdfBrush(windowColor), inner2, 2);

        if (part.index >= 0 && part.index < opts.list.length) {
          page1.drawLabel(opts.list[part.index].right, { rect: winRect, font: rightFont });
          page2.drawLabel(opts.list[part.index].left, { rect: winRect, font: leftFont });
        }
      }
    }

    page1.drawEllipse(
      GUtil.basePen,
      new PdfBrush(bushColor1),
      new PdfRect(r1.left - 25, r1.bottom - 20, 40, 30)
    );
    page1.drawEllipse(
      GUtil.basePen,
      new PdfBrush(bushColor2),
      new PdfRect(r1.left - 40, r1.bottom - 15, 25, 25)
    );

    page1.drawEllipse(
      GUtil.basePen,
      new PdfBrush(bushColor1),
      new PdfRect(r2.right - 15, r2.bottom - 25, 45, 35)
    );
    page1.drawEllipse(
      GUtil.basePen,
      new PdfBrush(bushColor2),
      new PdfRect(r2.right + 15, r2.bottom - 15, 25, 25)
    );

    const flowerRed = PdfColor.fromRgb(255, 100, 100);
    const flowerPink = PdfColor.fromRgb(255, 150, 200);
    const flowerYellow = PdfColor.fromRgb(255, 220, 100);
    const fColors = [flowerRed, flowerPink, flowerYellow];

    const leftFlowers = [
      new PdfPoint(r1.left - 20, r1.bottom - 15),
      new PdfPoint(r1.left - 10, r1.bottom - 10),
      new PdfPoint(r1.left - 30, r1.bottom - 5),
    ];
    const rightFlowers = [
      new PdfPoint(r2.right, r2.bottom - 15),
      new PdfPoint(r2.right + 10, r2.bottom - 20),
      new PdfPoint(r2.right + 20, r2.bottom - 10),
    ];

    for (const pt of leftFlowers) {
      if (rnd.next(0, 4) !== 0) {
        page1.drawEllipse(
          GUtil.basePen,
          new PdfBrush(fColors[rnd.next(0, fColors.length)]),
          new PdfRect(pt.x, pt.y, 6, 6)
        );
      }
    }
    for (const pt of rightFlowers) {
      if (rnd.next(0, 4) !== 0) {
        page1.drawEllipse(
          GUtil.basePen,
          new PdfBrush(fColors[rnd.next(0, fColors.length)]),
          new PdfRect(pt.x, pt.y, 6, 6)
        );
      }
    }
  }
}
